import { supabase } from "@/integrations/supabase/client";

export interface EmailRequestData {
  interviewee_name: string;
  interviewee_email: string;
  additional_emails?: string;
  question: string;
  context?: string;
  personalized_message?: string;
  email_subject?: string;
  intro_text?: string;
  call_to_action_text?: string;
  template_id?: string;
}

export interface EmailResponse {
  success: boolean;
  request_id?: string;
  email_id?: string;
  request_link?: string;
  error?: string;
}

/**
 * Service for sending interview request emails
 */
export class EmailService {
  /**
   * Send an interview request email
   */
  static async sendInterviewRequestEmail(data: EmailRequestData): Promise<EmailResponse> {
    try {
      console.log('Sending interview request email to:', data.interviewee_email);
      
      const { data: response, error } = await supabase.functions.invoke('send-interview-request', {
        body: data
      });

      if (error) {
        console.error('Error calling send-interview-request function:', error);
        throw new Error(`Failed to send email: ${error.message}`);
      }

      if (!response.success) {
        throw new Error(response.error || 'Unknown error occurred');
      }

      console.log('Email sent successfully:', response);
      return response;
    } catch (error) {
      console.error('Error in sendInterviewRequestEmail:', error);
      throw error;
    }
  }

  /**
   * Get email logs for an interview request
   */
  static async getEmailLogs(interviewRequestId?: string) {
    try {
      let query = supabase
        .from('email_logs')
        .select('*')
        .order('sent_at', { ascending: false });

      if (interviewRequestId) {
        query = query.eq('interview_request_id', interviewRequestId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching email logs:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error in getEmailLogs:', error);
      throw error;
    }
  }

  /**
   * Get all email logs with pagination
   */
  static async getAllEmailLogs(limit = 50, offset = 0) {
    try {
      const { data, error } = await supabase
        .from('email_logs')
        .select(`
          *,
          interview_requests (
            interviewee_name,
            interviewee_email,
            question
          )
        `)
        .order('sent_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (error) {
        console.error('Error fetching all email logs:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error in getAllEmailLogs:', error);
      throw error;
    }
  }
}